import Button from "@/components/ui/button";
import { ModalProps } from "@/types/component";

import "./index.scss";

import { FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";

// Modal stack to manage z-index and ESC key handling
let modalStack: string[] = [];

const Modal: FC<ModalProps> = ({
  children,
  customFooter,
  customHeader,
  formId = "modalForm",
  handleSubmit,
  isMultiStep = false,
  isOpen,
  loading = false,
  modalId = Math.random().toString(),
  onClose,
  onStepChange,
  onStepValidation,
  size = "md",
  stepSizes,
  stepContent,
  submitButtonText = "",
  isFormValid = true,
  title,
  withForm = false,
  showBackButton = false,
  onBack,
  hideStepProgress = false,
  currentStep: controlledStep,
  dismissible = true,
}) => {
  const { t } = useTranslation();
  const [internalStep, setInternalStep] = useState(0);
  const totalSteps = stepContent?.length || 0;
  const currentStep =
    controlledStep !== undefined ? controlledStep : internalStep;
  const setCurrentStep =
    controlledStep !== undefined ? () => {} : setInternalStep;
  const [zIndex, setZIndex] = useState(1000);

  useEffect(() => {
    if (isOpen) {
      modalStack.push(modalId);
      setZIndex(1000 + modalStack.length);
    } else {
      modalStack = modalStack.filter((id) => id !== modalId);
    }

    return () => {
      modalStack = modalStack.filter((id) => id !== modalId);
    };
  }, [isOpen, modalId]);

  useEffect(() => {
    if (!dismissible) return;

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        if (modalStack[modalStack.length - 1] === modalId) {
          onClose();
        }
      }
    };

    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, onClose, modalId, dismissible]);

  useEffect(() => {
    if (isOpen && controlledStep === undefined) {
      setInternalStep(0);
    }
  }, [isOpen, controlledStep]);

  if (!isOpen) return null;

  const handleSubmitWithLoading = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    if (loading) return;

    if (handleSubmit) {
      handleSubmit(event);
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      if (onStepValidation && !onStepValidation(currentStep)) {
        return; // Validation başarısızsa step geçişini engelle
      }

      setCurrentStep(currentStep + 1);
      if (onStepChange) onStepChange(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      if (onStepChange) onStepChange(currentStep - 1);
    }
  };

  const isLastStep = currentStep === totalSteps - 1;

  // Calculate current size based on step if stepSizes is provided
  const getCurrentSize = (): "sm" | "md" | "lg" | "xl" | "fullscreen" => {
    if (isMultiStep && stepSizes && stepSizes.length > 0) {
      // Use step-specific size if available, otherwise fallback to default size
      return stepSizes[currentStep] || stepSizes[stepSizes.length - 1] || size;
    }
    return size;
  };

  const currentSize = getCurrentSize();

  const renderContent = () => {
    if (isMultiStep && stepContent && stepContent.length > 0) {
      return stepContent[currentStep];
    }
    return children;
  };

  const renderFooter = () => {
    if (customFooter === null || customFooter === false) return null;
    if (customFooter !== undefined) return customFooter;

    if (isMultiStep && stepContent && stepContent.length > 0) {
      return (
        <>
          {currentStep > 0 && (
            <Button
              color="primary"
              onClick={handleBack}
              type="button"
              variant={"outlined"}
            >
              {t("buttons.back")}
            </Button>
          )}

          {!isLastStep ? (
            <>
              {currentStep === 0 && (
                <Button
                  color="secondary"
                  onClick={onClose}
                  type="button"
                  variant="text"
                >
                  {t("buttons.cancel")}
                </Button>
              )}
              <Button
                color="primary"
                onClick={handleNext}
                type="button"
                variant={"contained"}
              >
                {t("buttons.next")}
              </Button>
            </>
          ) : (
            <>
              {withForm ? (
                <Button
                  color="primary"
                  disabled={loading}
                  form={formId}
                  type="submit"
                  variant={"contained"}
                  loading={loading}
                >
                  {submitButtonText || t("buttons.submit")}
                </Button>
              ) : (
                <Button
                  color="primary"
                  disabled={loading}
                  onClick={() =>
                    handleSubmit &&
                    handleSubmit({} as React.FormEvent<HTMLFormElement>)
                  }
                  type="button"
                  variant={"contained"}
                  loading={loading}
                >
                  {submitButtonText || t("buttons.submit")}
                </Button>
              )}
            </>
          )}
        </>
      );
    }

    return (
      <>
        <Button
          color="secondary"
          onClick={onClose}
          type="button"
          variant="text"
        >
          {t("buttons.cancel")}
        </Button>
        {withForm ? (
          <Button
            color="primary"
            disabled={loading || !isFormValid}
            form={formId}
            type="submit"
            variant={"contained"}
            loading={loading}
          >
            {submitButtonText || t("buttons.submit")}
          </Button>
        ) : (
          <Button
            color="primary"
            disabled={loading || !isFormValid}
            type="button"
            variant={"soft"}
            loading={loading}
          >
            {submitButtonText || t("buttons.submit")}
          </Button>
        )}
      </>
    );
  };

  const renderHeader = () => {
    if (customHeader) {
      return customHeader;
    }

    return (
      <div className="modal-header">
        <div className="modal-header-left">
          {showBackButton && (
            <button className="back-button" onClick={onBack}>
              ←
            </button>
          )}
          <h3>{title}</h3>
        </div>
        {dismissible && (
          <div className="modal-header-actions">
            <button className="close-button" onClick={onClose}>
              ✕
            </button>
          </div>
        )}
      </div>
    );
  };

  return createPortal(
    <div className={`modal ${loading ? "loading" : ""}`} style={{ zIndex }}>
      <div className={`modal-content ${currentSize}-size`}>
        {renderHeader()}
        {isMultiStep &&
          stepContent &&
          stepContent.length > 0 &&
          !hideStepProgress && (
            <div className="modal-step-progress">
              <div className="step-progress-bar">
                <div
                  className="step-progress-item"
                  style={{
                    width: `${((currentStep + 1) / stepContent.length) * 100}%`,
                  }}
                />
              </div>
            </div>
          )}
        <div className="modal-body">{renderContent()}</div>
        {(() => {
          const footerContent = renderFooter();
          return footerContent ? (
            <div className="modal-footer">{footerContent}</div>
          ) : null;
        })()}
        {withForm && (
          <form
            id={formId}
            onSubmit={handleSubmitWithLoading}
            style={{ display: "none" }}
          />
        )}
        {loading && <div className="spinner" />}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
